<template>
  <span v-for="(breadcrumb, breadcrumbKey) in breadcrumbs" v-bind:key="breadcrumb"
        style="display: inline-block; margin-top: 7px; cursor: pointer;"
        @click.prevent="toggleBC">
    <strong v-if="breadcrumbKey > 0">&nbsp;|&nbsp;</strong>
    <span v-for="(item, itemKey) in breadcrumb.data" v-bind:key="item">
      <span v-if="itemKey > 0">
        <i class="pi pi-chevron-right" style="font-size: 0.7rem"/>
      </span>
      <span :style="(itemKey > 0 || breadcrumb.data.length === 1) && breadcrumb.id !== 1 ? 'color: #82B541;':''">
        {{ item.label }}
      </span>
    </span>
  </span>
  <span v-if="breadcrumbs.length === 0"
        style="color: #82B541; cursor: pointer;"
        @click.prevent="toggleBC"
  >
    {{ _t('ORGANIZATION') }}
  </span>
  <Dialog v-model:visible="breadcrumbsSelectDialog"
          :modal="true"
          :style="{width: '450px'}"
          header=" "
          @close="breadcrumbsSelectDialog = false"
  >
    <TreeTable
        v-if="!loadingOrganizations && !organizationLoadError && organizations.length > 0"
        v-model:expandedKeys="expandedOrganizations"
        v-model:selectionKeys="selectedOrgKeys"
        :selectionMode="isSingleSelection ? 'single' : 'checkbox'"
        :value="organizations"
        autoLayout
        class="p-treetable-sm"
        dynamic="true"
        @node-select="setOrgId($event)"
        @node-unselect="unselectOrgId($event)"
        @node-collapse="clearChildrenNodes($event)"
        @node-expand="appendNode($event)"
    >
      <Column :expander="true" field="name">
        <template #header>
          <div class="p-d-flex p-jc-between">
            <strong style="margin-top: .7rem;">
              {{ _t('label_orgs') }}
            </strong>
            <div class="p-formgroup-inline">
              <Button
                  v-if="!isSingleSelection"
                  v-tooltip.bottom="_t(withChildOrgsState ? 'label_exclude_child_orgs' : 'label_include_child_orgs')"
                  :class="withChildOrgsState ?
                      'p-button-rounded p-button-text p-button-lg p-button-secondary' :
                      'p-button-rounded p-button-text p-button-lg p-button-danger'"
                  @click="withChildOrgsState = !withChildOrgsState"
              >
                <i :class="withChildOrgsState ?
                      'mdi mdi-account-multiple-minus' :
                      'mdi mdi-account-multiple-plus'"
                   style="font-size: 16px;"
                />
              </Button>
              <Button v-tooltip.bottom="_t('Clear')"
                      class="p-button-rounded p-button-danger p-button-text p-button-lg"
                      @click="breadcrumbsInitState"
              >
                <i class="pi pi-filter-slash"
                   style="font-size: 16px;"
                />
              </Button>
            </div>
          </div>
        </template>
      </Column>
    </TreeTable>
    <div v-else-if="loadingOrganizations" class="p-p-3">
      {{ _t('label_loading') }}
    </div>
    <div v-else-if="organizationLoadError" class="p-p-3 p-text-bold">
      {{ organizationLoadError }}
    </div>
    <div v-else class="p-p-3">
      {{ _t('label_no_organizations_found') }}
    </div>
  </Dialog>
</template>
<script>
import {getTree} from "@/api/tree";
import {getChild} from "../../api/alarmTable";
import {orgIdsToSelectedKeys, readOrganizationState} from "@/storage/organizationStorage";
import {
  getOrganizationSnapshot,
  setMultiOrganizations,
  setSingleOrganization,
  setWithChildOrgs
} from "@/state/organizationContext";
import { formatApiError } from "@/utils/apiError";

export default {
  name: "MultiSelectableBreadcrumb",
  emits: ['change', 'selected', 'update:modelValue', 'update:withChildOrgs'],
  props: {
    modelValue: {
      type: [Array, Object, String, Number],
      default: null
    },
    mode: {
      type: String,
      default: null,
      validator: (value) => value === null || ['single', 'multi'].includes(value)
    },
    withChildOrgs: {
      type: Boolean,
      default: null
    },
    singleSelection: {
      type: Boolean,
      default: true
    },
    treeParams: {
      type: String,
      default: ''
      // default: 'category_filter=GEOGRAPHICAL_SCOPE&category_filter=BUILDING&category_filter=COMPANY_ORGANISATION&category_filter=ROOM',
    }
  },
  data() {
    const organizationState = readOrganizationState()
    const isSingleSelection = this.mode ? this.mode === 'single' : this.singleSelection
    const breadcrumbs = isSingleSelection ? organizationState.orgData : organizationState.multiOrgData

    return {
      breadcrumbs,
      selectedOrgKeys: organizationState.multiOrgSelectedKeys,
      organizations: [],
      expandedOrganizations: {},
      breadcrumbsSelectDialog: false,
      withChildOrgsState: this.withChildOrgs ?? organizationState.withChildOrgs,
      loadingOrganizations: false,
      organizationLoadError: null
    }
  },
  computed: {
    isSingleSelection() {
      return this.mode ? this.mode === 'single' : this.singleSelection
    }
  },
  watch: {
    modelValue(value) {
      this.applyModelValue(value)
    },
    selectedOrgKeys(value) {
      this.selectOrgKeysProcess(value)
    },
    withChildOrgs(value) {
      if (typeof value === 'boolean' && value !== this.withChildOrgsState) {
        this.withChildOrgsState = value
      }
    },
    withChildOrgsState() {
      if (this.isSingleSelection === true && this.withChildOrgsState === true) {
        this.withChildOrgsState = false
        return
      }
      setWithChildOrgs(this.withChildOrgsState)
      this.$emit('update:withChildOrgs', this.withChildOrgsState)
      this.breadcrumbsInitState()
      this.selectOrgKeysProcess(this.selectedOrgKeys)
    }
  },
  mounted() {
    this.applyModelValue(this.modelValue)
    this.getOrganizations()
  },
  methods: {
    applyModelValue(value) {
      if (value === null || typeof value === 'undefined') return

      if (this.isSingleSelection) {
        this.selectedOrgKeys = value
        return
      }

      this.selectedOrgKeys = orgIdsToSelectedKeys(value)
    },
    emitSelectionChange(orgIds = []) {
      if (this.isSingleSelection) {
        const selectedOrg = this.breadcrumbs[0] ?? null
        const orgId = selectedOrg?.id ?? null
        this.$emit('update:modelValue', orgId)
        this.$emit('change', orgId)
        this.$emit('selected', {
          mode: 'single',
          orgId,
          breadcrumbs: this.breadcrumbs,
          withChildOrgs: false,
        })
        return
      }

      this.$emit('update:modelValue', orgIds)
      this.$emit('change', JSON.stringify(this.breadcrumbs))
      this.$emit('selected', {
        mode: 'multi',
        orgIds,
        breadcrumbs: this.breadcrumbs,
        withChildOrgs: this.withChildOrgsState,
      })
    },
    breadcrumbsInitState() {
      this.orgFilter = []
      this.selectedOrgKeys = {}
      this.breadcrumbs = []
      if (this.isSingleSelection === true) {
        const rootOrg = this.organizations[0] ?? null
        setSingleOrganization(rootOrg, this.breadcrumbs)
        this.$emit('update:modelValue', rootOrg?.key ?? null)
        this.$emit('change', rootOrg?.key ?? null)
      } else {
        setMultiOrganizations({
          selectedKeys: {},
          orgIds: [],
          breadcrumbs: this.breadcrumbs,
          withChildOrgs: this.withChildOrgsState
        })
        this.emitSelectionChange([])
      }
    },
    toggleBC() {
      this.breadcrumbsSelectDialog = true
    },
    getOrganizations() {
      this.loadingOrganizations = true
      this.organizationLoadError = null
      getTree(null, this.treeParams).then((response) => {
        this.organizations = response.data.map(item => {
          const container = {data: {}};
          container.data.name = item.name;
          container.leaf = !item.hasChildren
          container.key = item.id;
          container.data.parentNames = []
          return container;
        })
        if (this.organizations.length > 0) {
          this.appendNode(this.organizations[0])
          this.expandedOrganizations[this.organizations[0].key] = true;
          if (this.breadcrumbs.length === 0) {
            this.breadcrumbsInitState()
          }
        }
      }).catch((error) => {
        this.organizations = []
        this.organizationLoadError = formatApiError(error, 'Unable to load organizations', ': ')
      }).then(() => {
        this.loadingOrganizations = false
      })
    },
    setOrgId(item) {
      if (item.key === "1" && this.breadcrumbs.length > 0) {
        this.breadcrumbs = []
        setMultiOrganizations({
          selectedKeys: {},
          orgIds: [],
          breadcrumbs: this.breadcrumbs,
          withChildOrgs: this.withChildOrgsState
        })
        this.emitSelectionChange([])
      }
      let breadcrumb = item.data.parentNames.length > 0 ?
          [...item.data.parentNames, item.data.name] :
          [item.data.name]

      breadcrumb = breadcrumb.map(element => {
        const container = {};
        container.label = element ?? this._t('ORGANIZATION');

        return container;
      })

      let isPresent = this.breadcrumbs.find(obj => {
        return obj.id === item.key
      })
      if (isPresent === undefined) {
        this.breadcrumbs = [...this.breadcrumbs, {id: item.key, data: breadcrumb}]
      }

      if (item.data.parentNames !== undefined && item.data.parentNames.length > 0) {
        this.breadcrumbs = this.breadcrumbs.filter((bc) => {
          for (let key in item.data.parentNames) {
            if (bc.data.length === 1 && bc.data[0].label === item.data.parentNames[key]) {
              return false
            }
          }
          return true
        })
      }
    },
    unselectOrgId(item) {
      delete this.selectedOrgKeys[item.key]
      this.breadcrumbs = this.breadcrumbs.filter((brc) => brc.id !== item.key)

    },
    appendNode(item) {
      let names = []
      if (this.organizations.length > 0 && item.key !== this.organizations[0].key) {
        names = [...item.data.parentNames, item.data.name]
      }
      getTree(item.key, this.treeParams).then((response) => {
        item.children = response.data.map(element => {
          const container = {data: {}};
          container.data.name = element.name;
          container.leaf = !element.hasChildren
          container.key = element.id;
          container.data.parentNames = names
          return container;
        })
      }).catch((error) => {
        this.organizationLoadError = formatApiError(error, 'Unable to load child organizations', ': ')
      })
      this.selectedOrgKeys = getOrganizationSnapshot().multiOrgSelectedKeys
    },
    filterBreadcrumbs() {
      for (let key in this.breadcrumbs) {
        if (!Object.keys(this.selectedOrgKeys).includes(String(this.breadcrumbs[key].id))) {
          this.breadcrumbs.splice(key, 1)
        }
      }
      if (this.breadcrumbs.length === 0 && this.organizations.length > 0 && this.isSingleSelection && !getOrganizationSnapshot().orgData.length) {
        this.setOrgId(this.organizations[0])
      }
    },
    selectOrgKeysProcess(value) {
      if (!value) return;
      let multiOrgIds = {}
      for (let index in Object.values(value)) {
        if (Object.values(value)[index].checked) {
          if (this.withChildOrgsState === true) {
            getChild(Object.keys(value)[index]).then((response) => {
              response.data.map(
                  (value) => {
                    multiOrgIds[value] = {'checked': true}
                  }
              )
              this.manageOrgKeysProcess(value, multiOrgIds)
            }).catch((error) => {
              this.organizationLoadError = formatApiError(error, 'Unable to load child organizations', ': ')
            })
          } else {
            multiOrgIds[Object.keys(value)[index]] = Object.values(value)[index]
            this.manageOrgKeysProcess(value, multiOrgIds)
          }
        } else {
          this.manageOrgKeysProcess(value, multiOrgIds)
        }
      }
    },
    manageOrgKeysProcess(value, multiOrgIds) {
      this.filterBreadcrumbs()
      if (this.breadcrumbs.length === 0 && this.isSingleSelection === true && this.organizations.length > 0) {
        this.setOrgId(this.organizations[0])
      }
      if (this.isSingleSelection === true) {
        this.breadcrumbs = this.breadcrumbs.filter(Boolean);
        const selectedOrg = this.breadcrumbs[0] ?? null
        setSingleOrganization(selectedOrg?.id ?? null, this.breadcrumbs)
        this.emitSelectionChange()

      } else {
        this.breadcrumbs = this.breadcrumbs
            .filter(element => {
              if (this.breadcrumbs.length === 1 || (this.organizations.length > 0 && element.id !== this.organizations[0].key && this.breadcrumbs.length > 0)) {
                return true
              }
              return false
            })
            .map(element => {
              let container = {};
              container = element;
              return container;
            })
        const orgIds = Object.keys(multiOrgIds).map((item) => {
          return {'id': item}
        })
        setMultiOrganizations({
          selectedKeys: multiOrgIds,
          orgIds,
          breadcrumbs: this.breadcrumbs,
          withChildOrgs: this.withChildOrgsState
        })
        this.emitSelectionChange(orgIds)
      }
    },
    clearChildrenNodes(item) {
      item.children.map(value => {
        delete this.selectedOrgKeys[value.key]
        item.children = []
      });
      this.selectOrgKeysProcess(this.selectedOrgKeys)
    }
  }
}
</script>
